/**
 * Lets the routed page name itself in the shell's header.
 *
 * The header bar sits above the routed outlet, so the shell renders it — but
 * only the page knows what it is called. "Create New Post" is not derivable
 * from the composer's nav entry, which reads "Posts". This carries it up.
 *
 * Pages call `usePageHeader(...)`; a page that does not call it falls back to
 * the active tab's nav label, which is what every other tab wants.
 */

import { createContext, useContext, useEffect, useMemo, useState } from 'react';

export interface PageHeader {
  title: string;
  subtitle?: string;
}

interface PageHeaderContextValue {
  header: PageHeader | null;
  setHeader: (header: PageHeader | null) => void;
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null);

export function PageHeaderProvider({ children }: Readonly<{ children: React.ReactNode }>) {
  const [header, setHeader] = useState<PageHeader | null>(null);
  const value = useMemo(() => ({ header, setHeader }), [header]);

  return <PageHeaderContext.Provider value={value}>{children}</PageHeaderContext.Provider>;
}

/** Read the current header. For the shell; pages should use `usePageHeader`. */
export function usePageHeaderValue(): PageHeader | null {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error('usePageHeaderValue must be used inside PageHeaderProvider');
  return ctx.header;
}

/**
 * Publish this page's header, clearing it on unmount so the next tab does not
 * inherit the previous one's title.
 */
export function usePageHeader(header: PageHeader): void {
  const ctx = useContext(PageHeaderContext);
  if (!ctx) throw new Error('usePageHeader must be used inside PageHeaderProvider');
  const { setHeader } = ctx;

  useEffect(() => {
    setHeader(header);
    return () => setHeader(null);
    // `header` is a fresh object every render, so the effect keys off its
    // contents rather than its identity.
  }, [setHeader, header.title, header.subtitle]);
}
